import { MedplumClient } from "@medplum/core";

import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";

import { loadInsertableDotPhrases } from "@/dot-phrases/services/insertable-dot-phrases";
import {
  InsertableDotPhrase,
  LoadInsertableDotPhrasesFailure,
} from "@/dot-phrases/types/insertable-dot-phrase";

import { RootState } from "..";

export const DOT_PHRASE_CACHE_STATUS = {
  Idle: "idle",
  Loading: "loading",
  Loaded: "loaded",
  Failed: "failed",
} as const;

export type DotPhraseCacheStatus =
  (typeof DOT_PHRASE_CACHE_STATUS)[keyof typeof DOT_PHRASE_CACHE_STATUS];

export function buildDotPhraseCacheKey(
  practiceId: string | undefined,
  practitionerId: string | undefined,
): string | undefined {
  if (!practiceId || !practitionerId) return undefined;

  return `${practiceId}/${practitionerId}`;
}

interface LoadInsertableDotPhrasesArgs {
  medplum: MedplumClient;
  practiceId: string;
  practitionerId: string;
  cacheKey: string;
}

export const loadInsertableDotPhrasesThunk = createAsyncThunk<
  InsertableDotPhrase[],
  LoadInsertableDotPhrasesArgs,
  { state: RootState; rejectValue: LoadInsertableDotPhrasesFailure }
>(
  "dotPhrases/loadInsertable",
  async ({ medplum, practiceId, practitionerId }, { rejectWithValue }) => {
    const result = await loadInsertableDotPhrases({ medplum, practiceId, practitionerId });

    if (!result.ok) return rejectWithValue(result.reason);

    return result.data;
  },
  {
    condition: ({ cacheKey }, { getState }) => getState().dotPhrases.cacheKey !== cacheKey,
  },
);

const insertableDotPhrasesAdapter = createEntityAdapter({
  selectId: (dotPhrase: InsertableDotPhrase) => dotPhrase.id,
});

export const dotPhrasesSlice = createSlice({
  name: "dotPhrases",
  initialState: insertableDotPhrasesAdapter.getInitialState({
    status: DOT_PHRASE_CACHE_STATUS.Idle as DotPhraseCacheStatus,
    cacheKey: undefined as string | undefined,
    requestId: undefined as string | undefined,
  }),
  reducers: {
    insertableDotPhrasesInvalidated(state) {
      insertableDotPhrasesAdapter.removeAll(state);
      state.cacheKey = undefined;
      state.requestId = undefined;
      state.status = DOT_PHRASE_CACHE_STATUS.Idle;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadInsertableDotPhrasesThunk.pending, (state, action) => {
        insertableDotPhrasesAdapter.removeAll(state);
        state.cacheKey = action.meta.arg.cacheKey;
        state.requestId = action.meta.requestId;
        state.status = DOT_PHRASE_CACHE_STATUS.Loading;
      })
      .addCase(loadInsertableDotPhrasesThunk.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        insertableDotPhrasesAdapter.setAll(state, action.payload);
        state.status = DOT_PHRASE_CACHE_STATUS.Loaded;
      })
      .addCase(loadInsertableDotPhrasesThunk.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        insertableDotPhrasesAdapter.removeAll(state);
        state.status = DOT_PHRASE_CACHE_STATUS.Failed;
      });
  },
});

export const { insertableDotPhrasesInvalidated } = dotPhrasesSlice.actions;

export const { selectAll: selectInsertableDotPhrases } = insertableDotPhrasesAdapter.getSelectors(
  (state: RootState) => state.dotPhrases,
);

export const selectInsertableDotPhrasesCacheKey = (state: RootState) => state.dotPhrases.cacheKey;

export const selectInsertableDotPhrasesStatus = (state: RootState) => state.dotPhrases.status;

export default dotPhrasesSlice.reducer;
